import * as Yup from 'yup'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FormikHelpers, useFormik } from 'formik'
import { omit } from 'lodash'
import { Button, Card, CardContent, CardHeader, Grid, Stack, TextField } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import {
  AssetGroup,
  AssetGroupInput,
  AssetInput,
  Aurora,
  AuroraAsset,
  Device,
  Position,
  Project
} from '../../types'
import { AssetGroupType, AssetType } from '../../enums'
import { isRequired, mustBeString } from '../../utils/validation'
import { getLevelsByProjectId } from '../../redux/slices/levels'
import { getLiftsByProjectId } from '../../redux/slices/lifts'
import { createAsset, updateAsset } from '../../services/assetService'
import { createAuroraAssetGroup, updateAuroraAssetGroup } from '../../services/assetGroup'
import LevelSelect from '../LevelSelect'
import LiftSelect from '../LiftSelect'
import AssetPhotoDropzone from '../AssetPhotoDropzone'
import { createErrorNotification } from '../../redux/slices/notifications'
import { getDevice } from '../../services/deviceService'
import { isAuroraAssetGroup } from '../../utils/aurora'

export interface AssetGroupSetupStepProps {
  type: AssetGroupType
  assetGroup?: AssetGroup
  project: Project
  aurora: Aurora
  liftId?: number | null
  levelId?: number | null
  position?: Position
  onAuroraChange?: (aurora: Aurora) => void
  onAssetGroupChange?: (assetGroup: AssetGroup | null) => void
  onBack?: () => void
  onNext: () => void
  onCancel?: () => void
}

export default function AssetGroupSetupStep({
  type,
  assetGroup,
  project,
  aurora,
  liftId = null,
  levelId = null,
  position,
  onAuroraChange,
  onAssetGroupChange,
  onNext,
  onBack,
  onCancel
}: AssetGroupSetupStepProps) {
  // Check if the asset group type is valid.
  if (assetGroup !== undefined && assetGroup.type !== type) {
    throw new Error('Invalid asset group type.')
  }

  // Check if the asset group project matches the selected project.
  if (assetGroup !== undefined && assetGroup.projectId !== project.id) {
    throw new Error('Asset group project does not match the selected project.')
  }

  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The photo.
   */
  const [photo, setPhoto] = useState<File | null>(null)

  /**
   * The initial values.
   */
  const initialValues: AssetGroupInput = {
    type,
    name: assetGroup?.name || '',
    projectId: project.id || null,
    levelId,
    liftId,
    customData: {}
  }

  /**
   * The validation schema.
   */
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .typeError(mustBeString(t('asset:name')))
      .required(isRequired(t('asset:name')))
  })

  /**
   * The formik state.
   */
  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleSubmit: onSubmit,
    handleBlur,
    handleChange
  } = useFormik<AssetGroupInput>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit
  })

  /**
   * Check if the field has an error.
   */
  function isError(field: keyof AssetGroupInput): boolean {
    return Boolean(touched[field] && errors[field])
  }

  /**
   * Get the helper text for the field.
   */
  function getHelperText(field: keyof AssetGroupInput): string | undefined {
    return touched[field] ? errors[field] : undefined
  }

  /**
   * Handle the "submit" action.
   */
  async function handleSubmit(values: AssetGroupInput, helpers: FormikHelpers<AssetGroupInput>) {
    switch (type) {
      case AssetGroupType.AURORA:
        handleUpdateOrCreateAuroraAssetGroup(values, helpers)
        break
    }
  }

  async function handleUpdateOrCreateAuroraAssetGroup(
    values: AssetGroupInput,
    { setSubmitting }: FormikHelpers<AssetGroupInput>
  ) {
    if (aurora === undefined) {
      return
    }

    if (assetGroup !== undefined && !isAuroraAssetGroup(assetGroup)) {
      console.error('Asset group is not an aurora asset group.')
      return
    }

    try {
      setSubmitting(true)

      // Create a new asset group for the auroras.
      let auroraAssetGroup =
        assetGroup === undefined
          ? await createAuroraAssetGroup({
              type: AssetGroupType.AURORA,
              name: values.name,
              projectId: values.projectId,
              levelId: values.levelId,
              liftId: values.liftId
            })
          : await updateAuroraAssetGroup(assetGroup.id, {
              name: values.name,
              levelId: values.levelId,
              liftId: values.liftId,
              customData: {
                ...assetGroup.customData
              }
            })

      // Create or update the asset for the aurora.
      const auroraAsset = await updateOrCreateAuroraAsset(aurora, {
        name: aurora.id,
        project: values.projectId,
        company: aurora.companyId,
        level: values.levelId,
        lift: values.liftId
      })

      if (aurora.device) {
        let device = aurora.device

        // Fetch the device so that it is more likely to include the asset.
        try {
          device = await getDevice(aurora.device.name)
        } catch (error) {
          console.error(error)
        }

        // Create or update the asset for the device.
        const deviceAsset = await updateOrCreateDeviceAsset(aurora.device, {
          name: aurora.id,
          project: values.projectId,
          // FIXME: Eventhough the device is fetched, there is still risk that this will reset the owner if device was loaded without asset relation.
          // The solution is the allow updating the asset without providing the company, but this requires a backend change.
          company: device?.asset?.companyId ?? null,
          level: values.levelId,
          lift: values.liftId
        })

        aurora.device.asset = deviceAsset
        aurora.device.assetId = deviceAsset.id

        if (auroraAsset?.aurora) {
          // Omit aurora to avoid circular reference.
          auroraAsset.aurora.device = omit(aurora.device, ['aurora'])
          auroraAsset.aurora.deviceId = aurora.device.name
        }
      }

      aurora.asset = auroraAsset
      aurora.assetId = auroraAsset.id

      onAuroraChange?.(aurora)

      if (position !== undefined) {
        const assetIds = auroraAssetGroup?.assets?.map((asset) => asset.id) ?? []
        auroraAssetGroup = await updateAuroraAssetGroup(auroraAssetGroup.id, {
          assetIds: [...assetIds, auroraAsset.id],
          customData: {
            masterId: auroraAssetGroup?.customData?.masterId ?? aurora.id,
            positions: {
              ...assetGroup?.customData?.positions,
              [aurora.id]: {
                x: position.x,
                y: position.y
              }
            }
          }
        })
      }

      onAssetGroupChange?.(auroraAssetGroup)

      onNext()
    } catch (error) {
      console.error(error)
      dispatch(createErrorNotification(t('installAssetGroupError')))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Create or update the asset for the aurora.
   */
  async function updateOrCreateAuroraAsset(aurora: Aurora, values: AssetInput) {
    if (aurora.assetId === null) {
      return createAsset({
        type: AssetType.AURORA,
        name: values.name,
        company: values.company,
        project: values.project,
        level: values.level,
        lift: values.lift,
        auroraId: aurora.id,
        groupId: values.groupId
      }) as Promise<AuroraAsset>
    }

    return updateAsset(aurora.assetId, {
      name: values.name,
      company: values.company,
      project: values.project,
      level: values.level,
      lift: values.lift,
      auroraId: aurora.id,
      groupId: values.groupId
    }) as Promise<AuroraAsset>
  }

  /**
   * Create or update the asset for the device.
   */
  async function updateOrCreateDeviceAsset(device: Device, values: AssetInput) {
    if (device.assetId === null) {
      return createAsset({
        type: AssetType.DEVICE,
        name: values.name,
        project: values.project,
        company: values.company,
        level: values.level,
        lift: values.lift
      })
    }

    return updateAsset(device.assetId, {
      name: values.name,
      project: values.project,
      company: values.company,
      level: values.level,
      lift: values.lift
    })
  }

  /**
   * Load the levels.
   */
  function loadLevels(projectId: number, controller?: AbortController) {
    dispatch(getLevelsByProjectId(projectId, { signal: controller?.signal }))
  }

  /**
   * Load the lifts.
   */
  function loadLifts(projectId: number, controller?: AbortController) {
    dispatch(getLiftsByProjectId(projectId, { signal: controller?.signal }))
  }

  /**
   * Load levels and lifts when the project changes.
   */
  useEffect(() => {
    const controller = new AbortController()

    loadLevels(project.id, controller)
    loadLifts(project.id, controller)

    return () => controller.abort()
  }, [project.id])

  const handleBack = () => {
    onBack?.()
  }

  return (
    <Card>
      <CardHeader title={t('enterAssetGroupDetails')} />
      <CardContent>
        <form onSubmit={onSubmit}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t('asset:name')}
                value={values.name}
                error={isError('name')}
                helperText={getHelperText('name')}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <LevelSelect
                name="levelId"
                label={t('asset:level')}
                project={project}
                value={values.levelId}
                error={isError('levelId')}
                helperText={getHelperText('levelId')}
                disabled={isSubmitting}
                withAddOption={true}
                withClearSelection={true}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <LiftSelect
                project={project}
                name="liftId"
                label={t('asset:lift')}
                value={values.liftId}
                error={isError('liftId')}
                helperText={getHelperText('liftId')}
                disabled={isSubmitting}
                withAddOption={true}
                withClearSelection={true}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            {/*{!isSubmitting && (
              <Grid item xs={12}>
                <AssetPhotoDropzone value={photo} onChange={setPhoto} />
              </Grid>
            )}*/}

            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={2}
                sx={{ mt: 6 }}
              >
                <Button variant="text" color="inherit" onClick={handleBack}>
                  {t('back')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={type === AssetGroupType.AURORA && position === null}
                  loading={isSubmitting}
                >
                  {t('install')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  )
}
